import { z } from 'zod';
import type { Status } from './identity-contracts';
import type { OpaqueCursorPagination } from './authorization.types';
import type { IpgSystemId } from './types/third-party-system.enums';
import type { BusinessDetails, ProcessorConfig } from './backend_config.types';
import type {
    OpaqueCursorSearchRequest } from './omnisearch.types';
import {
    buildStoredJsonParser,
    defineStoredJsonContract,
    defineStoredJsonSemanticFieldValue,
    StoredJsonUnknownRecordSchema,
} from './stored-json-contract.types';
import { convertStoredJsonNumberToBoundedNumber } from './stored-json-number';

type StoredFeeConfigurationNumberKey = 'surcharge_pct'
    | 'convenience_fee';

type StoredFeeConfigurationVersionKey =
    | 'merchant_account_configuration_version'
    | 'logical_terminal_configuration_version'
    | 'terminal_device_configuration_version';

function normalizeStoredFeeConfigurationNumber(
    value: unknown,
    key: StoredFeeConfigurationNumberKey
): unknown {
    const conversionResult = convertStoredJsonNumberToBoundedNumber(value, {
        domain: `payment-infrastructure.${key}`,
        numericKind: 'bounded-binary64',
        minimum: 0,
        maximum: key === 'surcharge_pct' ? 100 : Number.MAX_VALUE,
    });
    return conversionResult.success ? conversionResult.value : value;
}

export function normalizeStoredFeeConfiguration(
    value: unknown
): unknown {
    const recordResult = StoredJsonUnknownRecordSchema.safeParse(value);
    if (!recordResult.success) {
        return value;
    }

    const normalized: Record<string, unknown> = { ...recordResult.data };
    for (const key of Object.keys(recordResult.data)) {
        if (key === 'surcharge_pct' || key === 'convenience_fee') {
            normalized[key] = normalizeStoredFeeConfigurationNumber(
                recordResult.data[key],
                key
            );
        }
    }
    return normalized;
}

export function normalizeStoredVersionedFeeConfiguration(
    value: unknown,
    versionKey: StoredFeeConfigurationVersionKey
): unknown {
    const recordResult = StoredJsonUnknownRecordSchema.safeParse(value);
    if (!recordResult.success) {
        return value;
    }

    const normalized: Record<string, unknown> = { ...recordResult.data };
    if (Object.prototype.hasOwnProperty.call(recordResult.data, versionKey)) {
        const versionResult = convertStoredJsonNumberToBoundedNumber(
            recordResult.data[versionKey],
            {
                domain: `payment-infrastructure.${versionKey}`,
                numericKind: 'safe-integer',
                minimum: 0,
                maximum: Number.MAX_SAFE_INTEGER,
            }
        );
        normalized[versionKey] = versionResult.success
            ? versionResult.value
            : recordResult.data[versionKey];
    }
    if (Object.prototype.hasOwnProperty.call(recordResult.data, 'config')) {
        normalized.config = normalizeStoredFeeConfiguration(
            recordResult.data.config
        );
    }
    return normalized;
}

export interface MerchantAccountConfiguration {
    allow_surcharge: boolean;
    allow_convenience_fee: boolean;
    allow_override_fee: boolean;
    enable_terminal_tipping: boolean;
    addressRequiredForCardPayment: boolean;
    surcharge_pct: number; // Default: 0, minimum:0, maximum:100, scale: percentage
    convenience_fee: number; // Default: 0, minimum:0, maximum:unspecified, scale: dollars
}

export const MerchantAccountConfigurationInputSchema: z.ZodObject<{
    allow_surcharge: z.ZodDefault<z.ZodBoolean>;
    allow_convenience_fee: z.ZodDefault<z.ZodBoolean>;
    allow_override_fee: z.ZodDefault<z.ZodBoolean>;
    enable_terminal_tipping: z.ZodDefault<z.ZodBoolean>;
    addressRequiredForCardPayment: z.ZodOptional<z.ZodBoolean>;
    allow_missing_billing_address: z.ZodOptional<z.ZodBoolean>;
    surcharge_pct: z.ZodDefault<z.ZodNumber>;
    convenience_fee: z.ZodDefault<z.ZodNumber>;
}> = z.object({
    allow_surcharge: z.boolean().default(false),
    allow_convenience_fee: z.boolean().default(false),
    allow_override_fee: z.boolean().default(false),
    enable_terminal_tipping: z.boolean().default(false),
    addressRequiredForCardPayment: z.boolean().optional(),
    allow_missing_billing_address: z.boolean().optional(),
    surcharge_pct: z.number().min(0).max(100).default(0),
    convenience_fee: z.number().min(0).default(0),
});

export const resolveEffectiveAddressRequiredForCardPayment = (
    value: z.input<typeof MerchantAccountConfigurationInputSchema>,
    merchantConfigAddressRequiredForCardPayment?: boolean
): boolean =>
    merchantConfigAddressRequiredForCardPayment ??
    value.addressRequiredForCardPayment ??
    (value.allow_missing_billing_address !== undefined
        ? !value.allow_missing_billing_address
        : true);

export const resolveMerchantAccountConfiguration = (
    value: z.input<typeof MerchantAccountConfigurationInputSchema>,
    merchantConfigAddressRequiredForCardPayment?: boolean
): MerchantAccountConfiguration => ({
    allow_surcharge: value.allow_surcharge ?? false,
    allow_convenience_fee: value.allow_convenience_fee ?? false,
    allow_override_fee: value.allow_override_fee ?? false,
    enable_terminal_tipping: value.enable_terminal_tipping ?? false,
    addressRequiredForCardPayment:
        resolveEffectiveAddressRequiredForCardPayment(
            value,
            merchantConfigAddressRequiredForCardPayment
        ),
    surcharge_pct: value.surcharge_pct ?? 0,
    convenience_fee: value.convenience_fee ?? 0,
});

export const parseMerchantAccountConfiguration = (
    value: unknown,
    merchantConfigAddressRequiredForCardPayment?: boolean
): MerchantAccountConfiguration =>
    resolveMerchantAccountConfiguration(
        MerchantAccountConfigurationInputSchema.parse(value),
        merchantConfigAddressRequiredForCardPayment
    );

export const MerchantAccountConfigurationSchema: z.ZodType<MerchantAccountConfiguration> =
    MerchantAccountConfigurationInputSchema.transform((value) =>
        resolveMerchantAccountConfiguration(value)
    );

export const MerchantAccountConfigurationStorageVersion = {
    V2: 2,
    V3: 3,
} as const;

export type MerchantAccountConfigurationStorageVersion =
    (typeof MerchantAccountConfigurationStorageVersion)[keyof typeof MerchantAccountConfigurationStorageVersion];

export type MerchantAccountConfigurationStorageV1 =
    z.input<ReturnType<typeof legacyMerchantAccountConfigurationSchema>>;

function legacyMerchantAccountConfigurationSchema() {
    return MerchantAccountConfigurationInputSchema.omit({
        enable_terminal_tipping: true,
    });
}

const LegacyMerchantAccountConfigurationSchema:
    z.ZodType<Omit<MerchantAccountConfiguration, 'enable_terminal_tipping'>> =
    legacyMerchantAccountConfigurationSchema().transform((value) => {
        const resolved = resolveMerchantAccountConfiguration(value);
        return {
            allow_surcharge: resolved.allow_surcharge,
            allow_convenience_fee: resolved.allow_convenience_fee,
            allow_override_fee: resolved.allow_override_fee,
            addressRequiredForCardPayment:
                resolved.addressRequiredForCardPayment,
            surcharge_pct: resolved.surcharge_pct,
            convenience_fee: resolved.convenience_fee,
        };
    });

export interface MerchantAccountConfigurationStorageV2 {
    readonly merchant_account_configuration_version:
        typeof MerchantAccountConfigurationStorageVersion.V2;
    readonly config: Omit<MerchantAccountConfiguration, 'enable_terminal_tipping'>;
}

export interface MerchantAccountConfigurationStorageV3 {
    readonly merchant_account_configuration_version:
        typeof MerchantAccountConfigurationStorageVersion.V3;
    readonly config: MerchantAccountConfiguration;
}

export type StoredMerchantAccountConfiguration =
    | MerchantAccountConfigurationStorageV1
    | MerchantAccountConfigurationStorageV2
    | MerchantAccountConfigurationStorageV3;

export const MerchantAccountConfigurationStorageVersionSchema:
    z.ZodType<MerchantAccountConfigurationStorageVersion> =
    z.union([
        z.literal(MerchantAccountConfigurationStorageVersion.V2),
        z.literal(MerchantAccountConfigurationStorageVersion.V3),
    ]);

export const MerchantAccountConfigurationStorageV1Schema:
    z.ZodType<MerchantAccountConfigurationStorageV1> =
    legacyMerchantAccountConfigurationSchema();

export const MerchantAccountConfigurationStorageV2Schema:
    z.ZodType<MerchantAccountConfigurationStorageV2> =
    z.object({
        merchant_account_configuration_version: z.literal(
            MerchantAccountConfigurationStorageVersion.V2
        ),
        config: LegacyMerchantAccountConfigurationSchema,
    }).strict();

export const MerchantAccountConfigurationStorageV3Schema:
    z.ZodType<MerchantAccountConfigurationStorageV3> = z.object({
        merchant_account_configuration_version: z.literal(
            MerchantAccountConfigurationStorageVersion.V3
        ),
        config: MerchantAccountConfigurationSchema,
    }).strict();

export const StoredMerchantAccountConfigurationStorageV1Schema:
    z.ZodType<MerchantAccountConfigurationStorageV1> = z.preprocess(
        normalizeStoredFeeConfiguration,
        MerchantAccountConfigurationStorageV1Schema
    );

export const StoredMerchantAccountConfigurationStorageV2Schema:
    z.ZodType<MerchantAccountConfigurationStorageV2> = z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredVersionedFeeConfiguration(
                value,
                'merchant_account_configuration_version'
            ),
        MerchantAccountConfigurationStorageV2Schema
    );

export const StoredMerchantAccountConfigurationStorageV3Schema:
    z.ZodType<MerchantAccountConfigurationStorageV3> = z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredVersionedFeeConfiguration(
                value,
                'merchant_account_configuration_version'
            ),
        MerchantAccountConfigurationStorageV3Schema
    );

export function normalizeMerchantAccountConfigurationStorageV1(
    config: MerchantAccountConfigurationStorageV1
): MerchantAccountConfiguration {
    return {
        ...resolveMerchantAccountConfiguration(config),
        enable_terminal_tipping: false,
    };
}

export function normalizeMerchantAccountConfigurationStorageV2(
    config: MerchantAccountConfigurationStorageV2
): MerchantAccountConfiguration {
    return {
        ...config.config,
        enable_terminal_tipping: false,
    };
}

export function normalizeMerchantAccountConfigurationStorageV3(
    config: MerchantAccountConfigurationStorageV3
): MerchantAccountConfiguration {
    return config.config;
}

export const MerchantAccountConfigurationStorageContract =
    defineStoredJsonContract({
        contractName: 'MerchantAccountConfiguration',
        semanticFieldValues: {
            allowSurcharge:
                defineStoredJsonSemanticFieldValue<boolean>('scalar'),
            surchargePercent:
                defineStoredJsonSemanticFieldValue<number>('scalar'),
            enableTerminalTipping:
                defineStoredJsonSemanticFieldValue<boolean>('scalar'),
        },
        variants: {
            v1: {
                schemaName:
                    'StoredMerchantAccountConfigurationStorageV1Schema',
                schema: StoredMerchantAccountConfigurationStorageV1Schema,
                guards: [
                    {
                        kind: 'json_marker_absent',
                        markerPath: [
                            'merchant_account_configuration_version'],
                    },
                ],
                normalize: normalizeMerchantAccountConfigurationStorageV1,
                semanticPaths: {
                    allowSurcharge: ['allow_surcharge'],
                    surchargePercent: ['surcharge_pct'],
                },
                unrepresentedSemanticFields: ['enableTerminalTipping'],
            },
            v2: {
                schemaName:
                    'StoredMerchantAccountConfigurationStorageV2Schema',
                schema: StoredMerchantAccountConfigurationStorageV2Schema,
                guards: [
                    {
                        kind: 'json_marker_equals',
                        markerPath: [
                            'merchant_account_configuration_version'],
                        markerValue:
                            MerchantAccountConfigurationStorageVersion.V2,
                    },
                ],
                normalize: normalizeMerchantAccountConfigurationStorageV2,
                semanticPaths: {
                    allowSurcharge: ['config', 'allow_surcharge'],
                    surchargePercent: ['config', 'surcharge_pct'],
                },
                unrepresentedSemanticFields: ['enableTerminalTipping'],
            },
            v3: {
                schemaName:
                    'StoredMerchantAccountConfigurationStorageV3Schema',
                schema: StoredMerchantAccountConfigurationStorageV3Schema,
                guards: [
                    {
                        kind: 'json_marker_equals',
                        markerPath: [
                            'merchant_account_configuration_version'],
                        markerValue:
                            MerchantAccountConfigurationStorageVersion.V3,
                    },
                ],
                normalize: normalizeMerchantAccountConfigurationStorageV3,
                semanticPaths: {
                    allowSurcharge: ['config', 'allow_surcharge'],
                    surchargePercent: ['config', 'surcharge_pct'],
                    enableTerminalTipping: [
                        'config',
                        'enable_terminal_tipping'],
                },
                unrepresentedSemanticFields: [],
            },
        },
        writerVariant: 'v3',
    });

export const StoredMerchantAccountConfigurationSchema:
    z.ZodType<StoredMerchantAccountConfiguration> =
    z.union([
        MerchantAccountConfigurationStorageContract.variants.v3.schema,
        MerchantAccountConfigurationStorageContract.variants.v2.schema,
        MerchantAccountConfigurationStorageContract.variants.v1.schema,
    ]);

export const StoredMerchantAccountConfigurationRowParser:
    z.ZodType<MerchantAccountConfiguration> =
    buildStoredJsonParser(MerchantAccountConfigurationStorageContract);

export const StoredMerchantAccountConfigurationParser:
    z.ZodType<MerchantAccountConfiguration> =
    z.preprocess(
        (value: unknown): unknown => ({ value }),
        StoredMerchantAccountConfigurationRowParser
    );

export function serializeMerchantAccountConfigurationV2(
    config: Omit<MerchantAccountConfiguration, 'enable_terminal_tipping'>
): MerchantAccountConfigurationStorageV2 {
    return {
        merchant_account_configuration_version:
            MerchantAccountConfigurationStorageVersion.V2,
        config: LegacyMerchantAccountConfigurationSchema.parse(config),
    };
}

export function serializeMerchantAccountConfigurationV3(
    config: MerchantAccountConfiguration
): MerchantAccountConfigurationStorageV3 {
    return {
        merchant_account_configuration_version:
            MerchantAccountConfigurationStorageVersion.V3,
        config: MerchantAccountConfigurationSchema.parse(config),
    };
}

// Logical terminal: One or more terminals feeding transactions into a collective batch
export interface LogicalTerminalConfiguration {
    allow_override_fee?: boolean;
    surcharge_pct?: number;
    convenience_fee?: number;
}

// Terminal device: Either a physical card reader or a gateway endpoint (aka "virtual terminal")
export interface TerminalDeviceConfiguration {
    allow_override_fee?: boolean;
    surcharge_pct?: number;
    convenience_fee?: number;
}

export const LogicalTerminalConfigurationStorageVersion = {
    V2: 2,
} as const;

export type LogicalTerminalConfigurationStorageVersion =
    (typeof LogicalTerminalConfigurationStorageVersion)[keyof typeof LogicalTerminalConfigurationStorageVersion];

export type LogicalTerminalConfigurationStorageV1 =
    LogicalTerminalConfiguration;

export interface LogicalTerminalConfigurationStorageV2 {
    readonly logical_terminal_configuration_version:
        typeof LogicalTerminalConfigurationStorageVersion.V2;
    readonly config: LogicalTerminalConfiguration;
}

export type StoredLogicalTerminalConfiguration =
    | LogicalTerminalConfigurationStorageV1
    | LogicalTerminalConfigurationStorageV2;

export const TerminalDeviceConfigurationStorageVersion = {
    V2: 2,
} as const;

export type TerminalDeviceConfigurationStorageVersion =
    (typeof TerminalDeviceConfigurationStorageVersion)[keyof typeof TerminalDeviceConfigurationStorageVersion];

export type TerminalDeviceConfigurationStorageV1 =
    TerminalDeviceConfiguration;

export interface TerminalDeviceConfigurationStorageV2 {
    readonly terminal_device_configuration_version:
        typeof TerminalDeviceConfigurationStorageVersion.V2;
    readonly config: TerminalDeviceConfiguration;
}

export type StoredTerminalDeviceConfiguration =
    | TerminalDeviceConfigurationStorageV1
    | TerminalDeviceConfigurationStorageV2;

export interface ProcessorListItem {
    processor_uuid: string;
    processor_name: string;
    status: Status;
}

export interface ProcessorSearchPageRequest {
    q?: string;
    limit?: number;
    cursor?: string;
}

export interface ProcessorSearchRequest extends OpaqueCursorSearchRequest {
    organization_uuid?: string;
    show_deleted?: boolean;
}

export interface ProcessorSearchPageResponse {
    processors: ProcessorListItem[];
    pagination: OpaqueCursorPagination;
}

export interface CreateProcessorRequest {
    processor_name: string;
    processor_config: ProcessorConfig;
}

export interface GetProcessorResponse extends ProcessorListItem {
    processor_config: ProcessorConfig;
}

export interface UpdateProcessorRequest {
    processor_name?: string;
    processor_config?: ProcessorConfig;
    expected_processor_config?: ProcessorConfig;
    status?: Status;
}

export interface MerchantAccountSearchListItem {
    merchant_account_uuid: string;
    processor_name: string;
    processor_mid: string;
    organization_uuid: string;
    organization_name: string;
    businessDetails?: BusinessDetails;
    applies_to_children: boolean;
    status: Status;
    allow_surcharge: boolean;
    surcharge_pct: number;
    duplicate_processor_mid_count: number;
    has_duplicate_processor_mid: boolean;
}

export interface MerchantAccountSearchPageRequest {
    q?: string;
    limit?: number;
    cursor?: string;
    duplicates_only?: boolean;
    merchant_organization_uuid?: string;
}

export interface MerchantAccountSearchRequest extends OpaqueCursorSearchRequest {
    duplicates_only?: boolean;
    show_deleted?: boolean;
    merchant_organization_uuid?: string;
    organization_uuid?: string;
    include_descendants?: boolean;
}

export interface MerchantAccountSearchPageResponse {
    merchant_accounts: MerchantAccountSearchListItem[];
    pagination: OpaqueCursorPagination;
    duplicate_summary: {
        has_duplicates: boolean;
        duplicate_records_count: number;
    };
}

export interface MerchantAccountListItem extends MerchantAccountSearchListItem {
    merchant_account_id: number;
    organization_id: number;
}

export interface CreateMerchantAccountRequest {
    processor_name: string;
    processor_mid: string;
    organization_uuid: string;
    merchant_account_configuration: MerchantAccountConfiguration;
}

export interface UpdateMerchantAccountRequest {
    processor_name?: string;
    processor_mid?: string;
    applies_to_children?: boolean;
    status?: Status;
    merchant_account_configuration?: MerchantAccountConfiguration;
    expected_merchant_account_configuration?: MerchantAccountConfiguration;
}

export interface GetMerchantAccountResponse extends MerchantAccountSearchListItem {
    merchant_account_configuration: MerchantAccountConfiguration;
}

export interface LogicalTerminalListItem {
    logical_terminal_uuid: string;
    merchant_account_uuid: string;
    name: string | null;
    ipg_system_id: IpgSystemId;
    processor_name: string;
    processor_mid: string;
    processor_tid: string;
    businessDetails?: BusinessDetails;
    applies_to_children: boolean | null;
    status: Status;
    duplicate_processor_tid_count: number;
    has_duplicate_processor_tid: boolean;
}

export interface LogicalTerminalSearchPageRequest {
    q?: string;
    limit?: number;
    cursor?: string;
    duplicates_only?: boolean;
    merchant_account_uuid?: string;
}

export interface LogicalTerminalSearchRequest extends OpaqueCursorSearchRequest {
    duplicates_only?: boolean;
    show_deleted?: boolean;
    merchant_account_uuid?: string;
    organization_uuid?: string;
}

export interface LogicalTerminalSearchPageResponse {
    logical_terminals: LogicalTerminalListItem[];
    pagination: OpaqueCursorPagination;
    duplicate_summary: {
        has_duplicates: boolean;
        duplicate_records_count: number;
    };
}

export interface MerchantOrganizationMerchantAccountSummaryItem {
    merchant_account_uuid: string;
    processor_name: string;
    processor_mid: string;
    status: Status;
    allow_surcharge: boolean;
    surcharge_pct: number;
}

export interface MerchantOrganizationListItem {
    organization_uuid: string;
    name: string;
    businessDetails?: BusinessDetails;
    merchant_accounts: MerchantOrganizationMerchantAccountSummaryItem[];
}

export interface MerchantOrganizationSearchPageRequest {
    root_organization_uuid: string;
    q?: string;
    limit?: number;
    cursor?: string;
}

export interface MerchantOrganizationSearchRequest extends OpaqueCursorSearchRequest {
    root_organization_uuid: string;
}

export interface MerchantOrganizationSearchPageResponse {
    merchant_organizations: MerchantOrganizationListItem[];
    pagination: OpaqueCursorPagination;
}

export interface PreviewLogicalTerminalOrganizationChangeRequest {
    organization_uuid?: string | null;
}

export interface LogicalTerminalOrganizationConflictItem {
    terminal_device_uuid: string;
    processor_device_id: string;
    organization_uuid: string;
    organization_name: string;
}

export interface PreviewLogicalTerminalOrganizationChangeResponse {
    conflicts: LogicalTerminalOrganizationConflictItem[];
}

export interface CreateLogicalTerminalRequest {
    merchant_account_uuid: string;
    ipg_system_id: IpgSystemId;
    name?: string | null;
    processor_tid: string;
    organization_uuid?: string | null;
    logical_terminal_configuration?: LogicalTerminalConfiguration;
    create_default_physical_terminal_device?: boolean;
    create_default_virtual_terminal_device?: boolean;
}

export interface UpdateLogicalTerminalRequest {
    name?: string | null;
    processor_tid?: string;
    organization_uuid?: string | null;
    applies_to_children?: boolean | null;
    clear_conflicting_terminal_device_organization_ids?: boolean;
    status?: Status;
    logical_terminal_configuration?: LogicalTerminalConfiguration;
    expected_logical_terminal_configuration?: LogicalTerminalConfiguration;
    credential_uuid?: string | null;
}

export interface GetLogicalTerminalResponse extends LogicalTerminalListItem {
    organization_uuid: string | null;
    logical_terminal_configuration: LogicalTerminalConfiguration;
    credential_uuid: string | null;
}

export interface TerminalDeviceListItem {
    terminal_device_uuid: string;
    logical_terminal_uuid: string;
    name: string | null;
    processor_name: string;
    processor_mid: string;
    processor_tid: string;
    processor_device_id: string;
    businessDetails?: BusinessDetails;
    is_virtual: boolean;
    status: Status;
    duplicate_processor_device_id_count: number;
    has_duplicate_processor_device_id: boolean;
}

export interface TerminalDeviceSearchPageRequest {
    q?: string;
    limit?: number;
    cursor?: string;
    duplicates_only?: boolean;
    logical_terminal_uuid?: string;
}

export interface TerminalDeviceSearchRequest extends OpaqueCursorSearchRequest {
    duplicates_only?: boolean;
    show_deleted?: boolean;
    logical_terminal_uuid?: string;
    organization_uuid?: string;
    include_descendants?: boolean;
}

export interface TerminalDeviceSearchPageResponse {
    terminal_devices: TerminalDeviceListItem[];
    pagination: OpaqueCursorPagination;
    duplicate_summary: {
        has_duplicates: boolean;
        duplicate_records_count: number;
    };
}

export interface CreateTerminalDeviceRequest {
    logical_terminal_uuid: string;
    processor_device_id: string;
    is_virtual: boolean;
    organization_uuid?: string | null;
    terminal_device_configuration?: TerminalDeviceConfiguration;
}

export interface UpdateTerminalDeviceRequest {
    name?: string | null;
    processor_device_id?: string;
    is_virtual?: boolean;
    organization_uuid?: string | null;
    status?: Status;
    terminal_device_configuration?: TerminalDeviceConfiguration;
    expected_terminal_device_configuration?: TerminalDeviceConfiguration;
    credential_uuid?: string | null;
}

export interface GetTerminalDeviceResponse extends TerminalDeviceListItem {
    organization_uuid: string | null;
    terminal_device_configuration: TerminalDeviceConfiguration;
    credential_uuid: string | null;
}
